import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { TenantContext } from '../context/TenantContext';
import API from '../api/axios';
import './CollegeAdminDashboard.css';

const PAGE_LIMIT = 10;
const ASSIGNMENT_PAGE_LIMIT = 10;

const BookIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
  </svg>
);

const Pagination = ({ page, totalPages, total, limit, onPageChange }) => {
  if (totalPages <= 1) return null;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const getRange = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (page > 3) pages.push('\u2026');
    for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) pages.push(p);
    if (page < totalPages - 2) pages.push('\u2026');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="ca-pagination">
      <span className="ca-pagination-info">Showing {start}\u2013{end} of {total}</span>
      <div className="ca-pagination-controls">
        <button className="ca-page-btn" onClick={() => onPageChange(page - 1)} disabled={page === 1}>{'\u2039'}</button>
        {getRange().map((p, i) =>
          p === '\u2026' ? (
            <span key={`e-${i}`} className="ca-page-ellipsis">{'\u2026'}</span>
          ) : (
            <button
              key={p}
              className={`ca-page-btn ${p === page ? 'ca-page-btn--active' : ''}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          )
        )}
        <button className="ca-page-btn" onClick={() => onPageChange(page + 1)} disabled={page === totalPages}>{'\u203a'}</button>
      </div>
    </div>
  );
};

const CollegeAdminSubjects = () => {
  const { user } = useContext(AuthContext);
  const { tenantType } = useContext(TenantContext);

  const [activeTab, setActiveTab] = useState('subjects');
  const [subjects, setSubjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);

  // Subject assignment state
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('moderator');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [modalSubjects, setModalSubjects] = useState([]);
  const [modalSubjectsLoading, setModalSubjectsLoading] = useState(false);
  const [assignPage, setAssignPage] = useState(1);
  const [assignMeta, setAssignMeta] = useState({ total: 0, totalPages: 0 });
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkSubjects, setBulkSubjects] = useState([]);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);

  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disabledSubjectIds, setDisabledSubjectIds] = useState(new Set());

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSubject, setNewSubject] = useState({
    subject_name: '',
    subject_code: '',
    description: '',
    department_id: ''
  });

  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const ROLES = [
    { value: 'moderator', label: 'Moderator' },
    { value: 'subject_matter_expert', label: 'Subject Matter Expert' },
    { value: 'examiner', label: 'Examiner' },
  ];

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const [deptRes, subjRes] = await Promise.all([
        API.get(`/public/departments?college_id=${user.college_id}`).catch(() => ({ data: { departments: [] } })),
        API.get('/subjects').catch(() => ({ data: { subjects: [] } }))
      ]);

      const depts = deptRes.data.departments || [];
      const deptIds = new Set(depts.map(d => String(d.department_id)));
      setDepartments(depts);

      const allSubjects = subjRes.data.subjects || [];
      const filtered = allSubjects.filter(s => deptIds.has(String(s.department_id)));

      const total = filtered.length;
      const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
      const safePage = Math.min(p, totalPages);
      const start = (safePage - 1) * PAGE_LIMIT;
      const paged = filtered.slice(start, start + PAGE_LIMIT);

      setSubjects(paged);
      setMeta({ total, totalPages });
      setPage(safePage);
    } catch {
      showToast('Failed to load subjects', 'error');
    } finally {
      setLoading(false);
    }
  }, [user.college_id]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  // Fetch users for subject assignment (paginated)
  const fetchUsersForAssignment = useCallback(async (p = 1) => {
    try {
      const deptParam = selectedDeptId ? `&department_id=${selectedDeptId}` : '';
      const res = await API.get(`/subjects/users-for-assignment?role=${selectedRole}&page=${p}&limit=${ASSIGNMENT_PAGE_LIMIT}${deptParam}`);
      if (res.data.success) {
        setUsers(res.data.users || []);
        setAssignMeta({ total: res.data.total ?? 0, totalPages: res.data.totalPages ?? 1 });
        setAssignPage(p);
      }
    } catch (err) {
      showToast('Failed to fetch users', 'error');
    }
  }, [selectedRole, selectedDeptId]);

  useEffect(() => {
    if (activeTab === 'assignment') {
      fetchUsersForAssignment(1);
    }
  }, [activeTab, selectedRole, selectedDeptId, fetchUsersForAssignment]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const canShowSettingsTab = () => {
    if (!settings) return false;
    if (tenantType !== 'university') return true;
    return settings.college_admin_accessible === true;
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const { data } = await API.get('/subjects/assignment-settings');
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSettingToggle = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    setSaving(true);
    try {
      await API.put('/subjects/assignment-settings', { [key]: value });
    } catch (err) {
      setSettings({ ...settings });
      console.error('Failed to update setting:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAssignPageChange = (p) => {
    fetchUsersForAssignment(p);
  };

  const handlePageChange = (p) => { setPage(p); fetchData(p); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSubject.department_id) {
      showToast('Please select a department', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const { data } = await API.post('/subjects', newSubject);
      if (data.success) {
        showToast('Subject created');
        setNewSubject({ subject_name: '', subject_code: '', description: '', department_id: '' });
        setShowCreateModal(false);
        setPage(1);
        fetchData(1);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create subject', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusToggle = async (subject, newStatus) => {
    try {
      const { data } = await API.patch(`/subjects/${subject.subject_id}/status`, { status: newStatus });
      if (data.success) {
        showToast(`Subject ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchData(page);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    const currentSubjects = user.assignedSubjects ? user.assignedSubjects.map(s => s.subject_id) : [];
    setSelectedSubjects(currentSubjects);
    setShowAssignModal(true);

    if (!user.department_id) {
      setModalSubjects([]);
      setDisabledSubjectIds(new Set());
      showToast('This user has no department assigned', 'error');
      return;
    }

    setModalSubjectsLoading(true);
    try {
      const [subjRes, settingsRes] = await Promise.all([
        API.get(`/subjects?department_id=${user.department_id}&status=active&limit=100`),
        API.get('/subjects/assignment-settings')
      ]);
      setModalSubjects(subjRes.data.subjects || []);
      if (settingsRes.data.settings && settingsRes.data.settings.allow_multiple_users_per_subject === false) {
        const { data } = await API.get(`/subjects/assigned-subjects-by-role?role=${user.role}&department_id=${user.department_id}`);
        const assignedSet = new Set(data.subject_ids || []);
        const currentSet = new Set(currentSubjects);
        const disabled = new Set([...assignedSet].filter(id => !currentSet.has(id)));
        setDisabledSubjectIds(disabled);
      } else {
        setDisabledSubjectIds(new Set());
      }
    } catch (err) {
      setModalSubjects([]);
      setDisabledSubjectIds(new Set());
      showToast('Failed to load subjects for this department', 'error');
    } finally {
      setModalSubjectsLoading(false);
    }
  };

  const handleSubjectToggle = (subjectId) => {
    setSelectedSubjects(prev => {
      if (prev.includes(subjectId)) {
        return prev.filter(id => id !== subjectId);
      } else {
        return [...prev, subjectId];
      }
    });
  };

  const handleAssignSubjects = async () => {
    if (!selectedUser || selectedSubjects.length === 0) {
      showToast('Please select at least one subject', 'error');
      return;
    }

    setAssignLoading(true);
    try {
      const response = await API.post('/subjects/assign-to-user', {
        user_id: selectedUser.user_id,
        subject_ids: selectedSubjects
      });

      if (response.data.success) {
        showToast(`Subjects assigned successfully to ${selectedUser.name}`);
        setShowAssignModal(false);
        fetchUsersForAssignment(assignPage);
      } else {
        showToast(response.data.message || 'Failed to assign subjects', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to assign subjects', 'error');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemoveSubjects = (user) => {
    if (!user.assignedSubjects || user.assignedSubjects.length === 0) {
      showToast('User has no subjects assigned', 'error');
      return;
    }
    setRemoveTarget(user);
  };

  const confirmRemoveSubjects = async () => {
    if (!removeTarget) return;

    setRemoveLoading(true);
    try {
      const response = await API.post('/subjects/remove-from-user', {
        user_id: removeTarget.user_id,
        subject_ids: removeTarget.assignedSubjects.map(s => s.subject_id)
      });

      if (response.data.success) {
        showToast(`Subjects removed successfully from ${removeTarget.name}`);
        setRemoveTarget(null);
        fetchUsersForAssignment(assignPage);
      } else {
        showToast(response.data.message || 'Failed to remove subjects', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove subjects', 'error');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleBulkAssign = async () => {
    if (bulkSubjects.length === 0) {
      showToast('Please select at least one subject', 'error');
      return;
    }

    setBulkAssignLoading(true);
    try {
      const response = await API.post('/subjects/bulk-assign', {
        role: selectedRole,
        department_id: selectedDeptId || undefined,
        subject_ids: bulkSubjects
      });

      if (response.data.success) {
        showToast(response.data.message || 'Bulk assignment successful');
        setShowBulkAssignModal(false);
        setBulkSubjects([]);
        fetchUsersForAssignment(assignPage);
      } else {
        showToast(response.data.message || 'Failed to bulk assign subjects', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to bulk assign subjects', 'error');
    } finally {
      setBulkAssignLoading(false);
    }
  };

  const handleBulkSubjectToggle = (subjectId) => {
    setBulkSubjects(prev => {
      if (prev.includes(subjectId)) {
        return prev.filter(id => id !== subjectId);
      } else {
        return [...prev, subjectId];
      }
    });
  };

  const deptMap = {};
  departments.forEach(d => { deptMap[d.department_id] = d; });

  return (
    <div className="ca-page">
      {toast && <div className={`ca-toast ca-toast--${toast.type}`}>{toast.msg}</div>}

      <div className="ca-page-header">
        <h1>Subjects</h1>
        <p>Manage subjects across all departments in your college</p>
      </div>

      <div className="ca-tab-content">
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
          <button
            onClick={() => setActiveTab('subjects')}
            style={{
              padding: '0.75rem 1.5rem',
              borderBottom: activeTab === 'subjects' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'subjects' ? '#3b82f6' : '#64748b',
              fontWeight: activeTab === 'subjects' ? 600 : 500,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.95rem'
            }}
          >
            Subject Management
          </button>
          <button
            onClick={() => setActiveTab('assignment')}
            style={{
              padding: '0.75rem 1.5rem',
              borderBottom: activeTab === 'assignment' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'assignment' ? '#3b82f6' : '#64748b',
              fontWeight: activeTab === 'assignment' ? 600 : 500,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.95rem'
            }}
          >
            Subject Assignment
          </button>
          {canShowSettingsTab() && (
            <button
              onClick={() => setActiveTab('settings')}
              style={{
                padding: '0.75rem 1.5rem',
                borderBottom: activeTab === 'settings' ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === 'settings' ? '#3b82f6' : '#64748b',
                fontWeight: activeTab === 'settings' ? 600 : 500,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.95rem'
              }}
            >
              Subject Settings
            </button>
          )}
        </div>

        {/* Subjects Tab */}
        {activeTab === 'subjects' && (
          <>
        <div className="ca-section-header">
          <div>
            <h2>Subject Management</h2>
            {meta.total > 0 && (
              <p>{meta.total} subject{meta.total !== 1 ? 's' : ''} total</p>
            )}
          </div>
          <button className="ca-btn-primary" onClick={() => setShowCreateModal(true)}>
            + Create Subject
          </button>
        </div>

        {loading ? (
          <div className="ca-spinner-wrap"><div className="ca-spinner" /></div>
        ) : subjects.length === 0 ? (
          <div className="ca-empty">
            <BookIcon />
            <h3>No subjects yet</h3>
            <p>Create your first subject to get started.</p>
          </div>
        ) : (
          <>
            <div className="ca-table-wrap">
              <table className="ca-table">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Code</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subjects.map(s => (
                    <tr key={s.subject_id}>
                      <td className="ca-td-name">{s.subject_name}</td>
                      <td><span className="ca-code-tag">{s.subject_code || '\u2014'}</span></td>
                      <td className="ca-muted">
                        {deptMap[s.department_id]?.department_name || s.department_name || '\u2014'}
                      </td>
                      <td>
                        <span className={`ca-status-badge ca-status-badge--${s.status || 'active'}`}>
                          {s.status || 'active'}
                        </span>
                      </td>
                      <td>
                        <div className="ca-action-row">
                          {s.status === 'active' ? (
                            <button
                              className="ca-btn-reject"
                              onClick={() => handleStatusToggle(s, 'inactive')}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              className="ca-btn-approve"
                              onClick={() => handleStatusToggle(s, 'active')}
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={PAGE_LIMIT}
              onPageChange={handlePageChange}
            />
          </>
        )}
          </>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div style={{ padding: '0.5rem 0' }}>
            <div className="ca-section-header">
              <div>
                <h2>Subject Assignment Settings</h2>
                <p>Configure how many faculty members can be assigned per subject</p>
              </div>
            </div>
            {settingsLoading ? (
              <div className="ca-spinner-wrap"><div className="ca-spinner" /></div>
            ) : !settings ? (
              <p style={{ color: '#64748b' }}>Failed to load settings.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '500px', marginTop: '0.5rem' }}>
                <SettingToggleField
                  label="Allow multiple users per subject"
                  description="When enabled, multiple SMEs, Examiners, and Moderators can be assigned to the same subject"
                  checked={settings.allow_multiple_users_per_subject}
                  onChange={(v) => handleSettingToggle('allow_multiple_users_per_subject', v)}
                  saving={saving}
                />
              </div>
            )}
          </div>
        )}

        {/* Assignment Tab */}
        {activeTab === 'assignment' && (
          <>
            <div className="ca-section-header">
              <div>
                <h2>Assign Subjects to {ROLES.find(r => r.value === selectedRole)?.label}</h2>
                <p>Manage subject assignments for your college</p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={selectedDeptId}
                  onChange={(e) => setSelectedDeptId(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Departments</option>
                  {departments.map(d => (
                    <option key={d.department_id} value={d.department_id}>
                      {d.department_name}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                {users.length > 0 && (
                  <button
                    className="ca-btn-primary"
                    onClick={() => {
                      setBulkSubjects([]);
                      setShowBulkAssignModal(true);
                    }}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Bulk Assign
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="ca-spinner-wrap"><div className="ca-spinner" /></div>
            ) : users.length === 0 ? (
              <div className="ca-empty">
                <BookIcon />
                <h3>No users found</h3>
                <p>No {ROLES.find(r => r.value === selectedRole)?.label.toLowerCase()} users in your college.</p>
              </div>
            ) : (
              <>
                <div className="ca-table-wrap">
                  <table className="ca-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Department</th>
                        <th>Assigned Subject</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.user_id}>
                          <td className="ca-td-name">{user.name}</td>
                          <td><a href={`mailto:${user.email}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>{user.email}</a></td>
                          <td className="ca-muted">{user.department_name || '—'}</td>
                          <td className="ca-muted">
                            {user.assignedSubjects && user.assignedSubjects.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                {user.assignedSubjects.map(s => (
                                  <div key={s.subject_id}>
                                    <strong>{s.subject_name}</strong>
                                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                      {s.subject_code}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#f59e0b' }}>Not assigned</span>
                            )}
                          </td>
                          <td>
                            <span style={{
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              backgroundColor: user.status === 'active' ? '#d1fae5' : '#fee2e2',
                              color: user.status === 'active' ? '#065f46' : '#991b1b'
                            }}>
                              {user.status}
                            </span>
                          </td>
                          <td>
                            <div className="ca-action-row">
                              <button
                                className="ca-btn-approve"
                                onClick={() => handleSelectUser(user)}
                              >
                                {user.assignedSubjects && user.assignedSubjects.length > 0 ? 'Change' : 'Assign'}
                              </button>
                              {user.assignedSubjects && user.assignedSubjects.length > 0 && (
                                <button
                                  className="ca-btn-reject"
                                  onClick={() => handleRemoveSubjects(user)}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={assignPage}
                  totalPages={assignMeta.totalPages}
                  total={assignMeta.total}
                  limit={ASSIGNMENT_PAGE_LIMIT}
                  onPageChange={handleAssignPageChange}
                />
              </>
            )}
          </>
        )}
      </div>

      {showCreateModal && (
        <div className="ca-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="ca-modal" onClick={e => e.stopPropagation()}>
            <div className="ca-modal-header">
              <h3>New Subject</h3>
              <button className="ca-modal-close" onClick={() => setShowCreateModal(false)}>{'\u00d7'}</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="ca-modal-body">
                <div className="ca-form-group">
                  <label className="ca-form-label">Department *</label>
                  <select
                    className="ca-form-input"
                    value={newSubject.department_id}
                    onChange={e => setNewSubject(s => ({ ...s, department_id: e.target.value }))}
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map(d => (
                      <option key={d.department_id} value={d.department_id}>
                        {d.department_name} ({d.department_code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ca-form-group">
                  <label className="ca-form-label">Subject Name *</label>
                  <input
                    type="text"
                    className="ca-form-input"
                    value={newSubject.subject_name}
                    onChange={e => setNewSubject(s => ({ ...s, subject_name: e.target.value }))}
                    placeholder="e.g., Data Structures"
                    required
                    autoFocus
                  />
                </div>
                <div className="ca-form-group">
                  <label className="ca-form-label">Subject Code</label>
                  <input
                    type="text"
                    className="ca-form-input"
                    value={newSubject.subject_code}
                    onChange={e => setNewSubject(s => ({ ...s, subject_code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., CS301"
                  />
                </div>
                <div className="ca-form-group">
                  <label className="ca-form-label">Description</label>
                  <textarea
                    className="ca-form-textarea"
                    value={newSubject.description}
                    onChange={e => setNewSubject(s => ({ ...s, description: e.target.value }))}
                    placeholder="Brief description of the subject"
                    rows={3}
                  />
                </div>
              </div>
              <div className="ca-modal-footer">
                <button type="button" className="ca-btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="ca-btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Creating\u2026' : 'Create Subject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && selectedUser && (
        <div className="ca-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="ca-modal" onClick={e => e.stopPropagation()}>
            <div className="ca-modal-header">
              <h3>Assign Subjects to {selectedUser.name}</h3>
              <button className="ca-modal-close" onClick={() => setShowAssignModal(false)}>{'\u00d7'}</button>
            </div>
            <div className="ca-modal-body">
              <div className="ca-form-group">
                <label className="ca-form-label">
                  Select Subjects ({selectedSubjects.length} selected)
                  {selectedUser.department_name && (
                    <span style={{ fontWeight: 400, color: '#64748b' }}> \u2014 {selectedUser.department_name}</span>
                  )}
                </label>
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.5rem' }}>
                  {modalSubjectsLoading ? (
                    <p style={{ color: '#64748b', padding: '1rem' }}>Loading subjects\u2026</p>
                  ) : modalSubjects.length === 0 ? (
                    <p style={{ color: '#64748b', padding: '1rem' }}>No subjects available in this department</p>
                  ) : (
                    modalSubjects.map(subject => {
                      const isDisabled = disabledSubjectIds.has(subject.subject_id);
                      return (
                      <label
                        key={subject.subject_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem',
                          borderBottom: '1px solid #f1f5f9',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.5 : 1
                        }}
                        title={isDisabled ? `This subject already has an assigned ${selectedUser.role === 'subject_matter_expert' ? 'SME' : selectedUser.role === 'examiner' ? 'Examiner' : 'Moderator'}` : ''}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSubjects.includes(subject.subject_id)}
                          onChange={() => !isDisabled && handleSubjectToggle(subject.subject_id)}
                          disabled={isDisabled}
                          style={{ marginRight: '0.75rem' }}
                        />
                        <div>
                          <div style={{ fontWeight: '500' }}>{subject.subject_name}</div>
                          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            {subject.subject_code}
                            {isDisabled && <span style={{ color: '#ef4444', marginLeft: '0.5rem' }}>(already assigned)</span>}
                          </div>
                        </div>
                      </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            <div className="ca-modal-footer">
              <button
                type="button"
                className="ca-btn-secondary"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ca-btn-primary"
                onClick={handleAssignSubjects}
                disabled={assignLoading || selectedSubjects.length === 0}
              >
                {assignLoading ? 'Assigning\u2026' : 'Assign Subjects'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {removeTarget && (
        <div className="ca-modal-overlay" onClick={() => !removeLoading && setRemoveTarget(null)}>
          <div className="ca-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="ca-modal-header">
              <h3>Remove Subjects</h3>
              <button className="ca-modal-close" onClick={() => !removeLoading && setRemoveTarget(null)}>{'\u00d7'}</button>
            </div>
            <div className="ca-modal-body">
              <p style={{ margin: '0 0 0.75rem', color: '#475569' }}>
                Are you sure you want to remove all assigned subjects from <strong>{removeTarget.name}</strong>?
              </p>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem' }}>
                {removeTarget.assignedSubjects.map(s => (
                  <div key={s.subject_id} style={{ fontSize: '0.875rem', color: '#991b1b' }}>
                    {s.subject_name} <span style={{ color: '#b91c1c', opacity: 0.8 }}>({s.subject_code})</span>
                  </div>
                ))}
              </div>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="ca-modal-footer">
              <button
                type="button"
                className="ca-btn-secondary"
                onClick={() => setRemoveTarget(null)}
                disabled={removeLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ca-btn-danger"
                onClick={confirmRemoveSubjects}
                disabled={removeLoading}
              >
                {removeLoading ? 'Removing\u2026' : 'Remove Subjects'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Modal */}
      {showBulkAssignModal && (
        <div className="ca-modal-overlay" onClick={() => !bulkAssignLoading && setShowBulkAssignModal(false)}>
          <div className="ca-modal" onClick={e => e.stopPropagation()}>
            <div className="ca-modal-header">
              <h3>Bulk Assign Subjects to {ROLES.find(r => r.value === selectedRole)?.label}s</h3>
              <button className="ca-modal-close" onClick={() => !bulkAssignLoading && setShowBulkAssignModal(false)}>{'\u00d7'}</button>
            </div>
            <div className="ca-modal-body">
              <div className="ca-form-group">
                <label className="ca-form-label">
                  Select Subjects to Assign ({bulkSubjects.length} selected)
                  {selectedDeptId && departments.find(d => String(d.department_id) === selectedDeptId) && (
                    <span style={{ fontWeight: 400, color: '#64748b' }}> — {departments.find(d => String(d.department_id) === selectedDeptId)?.department_name}</span>
                  )}
                </label>
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.75rem' }}>
                  These subjects will be assigned to all {ROLES.find(r => r.value === selectedRole)?.label.toLowerCase()} users
                  {selectedDeptId ? ' in the selected department' : ' in your college'}.
                </p>
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.5rem' }}>
                  {(() => {
                    const deptId = selectedDeptId || (departments.length > 0 ? departments[0].department_id : '');
                    const filteredSubjects = subjects.filter(s => String(s.department_id) === String(deptId));
                    return filteredSubjects.length === 0 ? (
                      <p style={{ color: '#64748b', padding: '1rem' }}>No subjects available{selectedDeptId ? ' in this department' : ''}</p>
                    ) : (
                      filteredSubjects.map(subject => (
                        <label
                          key={subject.subject_id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.75rem',
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={bulkSubjects.includes(subject.subject_id)}
                            onChange={() => handleBulkSubjectToggle(subject.subject_id)}
                            style={{ marginRight: '0.75rem' }}
                          />
                          <div>
                            <div style={{ fontWeight: '500' }}>{subject.subject_name}</div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                              {subject.subject_code}
                            </div>
                          </div>
                        </label>
                      ))
                    );
                  })()}
                </div>
              </div>
            </div>
            <div className="ca-modal-footer">
              <button
                type="button"
                className="ca-btn-secondary"
                onClick={() => setShowBulkAssignModal(false)}
                disabled={bulkAssignLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ca-btn-primary"
                onClick={handleBulkAssign}
                disabled={bulkAssignLoading || bulkSubjects.length === 0}
              >
                {bulkAssignLoading ? 'Assigning\u2026' : `Assign to ${users.length} user${users.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingToggleField = ({ label, description, checked, onChange, saving }) => (
  <label style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    opacity: saving ? 0.6 : 1
  }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={saving}
      style={{ marginTop: '0.2rem', flexShrink: 0 }}
    />
    <div>
      <div style={{ fontWeight: 500, color: '#1e293b' }}>{label}</div>
      {description && (
        <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>
          {description}
        </div>
      )}
    </div>
  </label>
);

export default CollegeAdminSubjects;
